import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { getContactRules, getContacts, updateContactRules } from "@api/contact.api";
import { getContactEmails } from "@api/portfolio.api";

import C from "@constants/constants";

import { createObjectFromSearchParameters } from "@hooks/useFilters";

import { ContactResponseType, ContactsSortRulesType, EmailDataType } from "@interfaces/contact.type";
import { ITechnology } from "@interfaces/portfolio.type";

import { useTableStore } from "@store/filters/filters.store";
import { PopupActions } from "@store/popupStore";

export const useGetContactsQuery = () => {
  const [parameters] = useSearchParams();

  const { companyIds } = createObjectFromSearchParameters(parameters);
  const { filters, pagination } = useTableStore("contact");

  const companyIdsToSearch = companyIds || [];

  return useQuery({
    enabled: companyIdsToSearch.length > 0, // Enable when we have companies to fetch
    placeholderData: keepPreviousData,
    queryFn: async () => {
      return await getContacts(companyIdsToSearch, {
        ...filters,
        ...pagination,
      });
    },
    queryKey: [
      "contacts",
      companyIdsToSearch,
      JSON.stringify({
        filters,
        pagination,
      }),
    ],
    staleTime: C.STALE_TIME,
  });
};

interface IGetContactEmailsInput {
  companyIds?: string[];
  peopleIds: string[];
  technologyId: string;
}

export const useFetchContactEmailsQuery = () => {
  const queryClient = useQueryClient();

  const [parameters] = useSearchParams();

  const { companyIds } = createObjectFromSearchParameters(parameters);
  const { filters, pagination } = useTableStore("contact");
  const companyIdsKey = companyIds || [];

  return useMutation<EmailDataType[], Error, IGetContactEmailsInput, ITechnology>({
    mutationFn: async ({ peopleIds }) => {
      return await getContactEmails(peopleIds);
    },
    onError: () => {
      PopupActions.openAlert({
        duration: 4000,
        message: "Error fetching contact email(s). Please try again in one minute.",
        severity: "error",
      });
    },
    onSuccess: (fetchedEmails, { companyIds, peopleIds, technologyId }) => {
      const technology = queryClient.getQueryData<ITechnology>(["technology", technologyId]);

      if (technology) {
        const updatedTechnologyContacts = technology.contacts.map((contact) => {
          if (peopleIds.includes(contact.id)) {
            const fetchedEmail = fetchedEmails.find((email) => email.id === contact.id);
            return {
              ...contact,
              email: fetchedEmail ? fetchedEmail.email : null,
              emailStatus: fetchedEmail ? fetchedEmail.emailStatus : "unavailable",
            };
          }
          return contact;
        });
        queryClient.setQueryData(["technology", technologyId], {
          ...technology,
          contacts: updatedTechnologyContacts,
        });
      }

      if (companyIds?.length) {
        queryClient.setQueryData(
          [
            "contacts",
            companyIdsKey,
            JSON.stringify({
              filters,
              pagination,
            }),
          ],
          (oldData: ContactResponseType) => {
            return {
              ...oldData,
              contacts: oldData.contacts.map((contact) => {
                if (peopleIds.includes(contact.id)) {
                  const fetchedEmail = fetchedEmails.find((email) => email.id === contact.id);
                  return {
                    ...contact,
                    email: fetchedEmail ? fetchedEmail.email : null,
                    emailStatus: fetchedEmail ? fetchedEmail.emailStatus : "unavailable",
                  };
                }
                return contact;
              }),
            };
          },
        );
      }

      if (fetchedEmails.length === 0 || !fetchedEmails[0]?.email) {
        PopupActions.openAlert({
          duration: 4000,
          message: "There appears to be no email for this contact. Please flag for review",
          severity: "error",
        });
      } else {
        PopupActions.openAlert({
          duration: 4000,
          message: "Email(s) fetched successfully.",
          severity: "success",
        });
      }
    },
  });
};

export const useGetContactScoringRulesQuery = () => {
  return useQuery({
    queryFn: async () => {
      return getContactRules();
    },
    queryKey: ["contactRules"],
    staleTime: C.STALE_TIME,
  });
};

interface IUpdateContactScoringRulesInput {
  newScoringRules: ContactsSortRulesType;
}

export const useUpdateContactScoringRulesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<ContactsSortRulesType, Error, IUpdateContactScoringRulesInput>({
    mutationFn: async ({ newScoringRules }) => {
      return await updateContactRules(newScoringRules);
    },
    onError: () => {
      PopupActions.openAlert({
        duration: 4000,
        message: "Error updating scoring rules. Please try again in one minute.",
        severity: "error",
      });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["contactRules"], data);
    },
  });
};
